import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, ImagePlus, Smile, X, Trash2, VolumeX, Volume2 } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveSocket } from "@/lib/useLiveSocket";
import { Lightbox, AutoTextarea } from "@/components/common";

const EMOJIS = ["🔥", "😍", "🎉", "💎", "👀", "⚡"];

export default function Chat() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => api.get("/chat").then(({ data }) => setMsgs(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  // live updates via WebSocket
  useLiveSocket(useCallback((evt) => {
    if (evt.type === "chat") {
      setMsgs((prev) => (prev.some((m) => m.id === evt.message.id) ? prev : [...prev, evt.message]));
    } else if (evt.type === "chat_react") {
      setMsgs((prev) => prev.map((m) => (m.id === evt.id ? { ...m, reactions: evt.reactions } : m)));
    } else if (evt.type === "chat_deleted") {
      setMsgs((prev) => prev.filter((m) => m.id !== evt.id));
    } else if (evt.type === "chat_cleared" || evt.type === "data_reset") {
      setMsgs([]);
    }
  }, []));

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const upload = async (file) => {
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/uploads", fd); setImage(data.path); }
    catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !image) return;
    try { await api.post("/chat", { text: text.trim(), image }); setText(""); setImage(null); }
    catch (err) { toast.error(apiError(err)); }
  };

  const react = async (id, emoji) => {
    setPickerFor(null);
    try { await api.post(`/chat/${id}/react`, null, { params: { emoji } }); } catch { /* noop */ }
  };

  const mute = async (m, hours) => {
    if (!window.confirm(`Mute ${m.user_name} for 24 hours? They won't be able to chat.`)) return;
    try { const { data } = await api.post(`/chat/${m.id}/mute`, null, { params: { hours: hours || 24 } }); toast.success(data.message); }
    catch (e) { toast.error(apiError(e)); }
  };

  const unmute = async (m) => {
    try { const { data } = await api.post(`/chat/${m.id}/unmute`); toast.success(data.message); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/chat/${m.id}`); setMsgs((prev) => prev.filter((x) => x.id !== m.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>
      <h1 className="font-display text-2xl font-black text-white mb-3">Community Chat</h1>
      <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pb-4" data-testid="chat-feed">
        {msgs.map((m) => {
          const mine = m.user_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-slate-300">{m.user_name}</span>
                  {m.role === "admin" && <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded gold-gradient text-obsidian">HOST</span>}
                </div>
                <div className={`relative group rounded-2xl px-4 py-2.5 ${mine ? "gold-gradient text-obsidian" : "card-surface text-white"}`} data-testid={`chat-msg-${m.id}`}>
                  {m.image && <img src={fileUrl(m.image)} alt="" onClick={() => setLightbox(fileUrl(m.image))} className="rounded-lg mb-2 max-h-60 object-cover cursor-zoom-in" data-testid={`chat-img-${m.id}`} />}
                  {m.text && <p className="text-sm break-words whitespace-pre-wrap">{m.text}</p>}
                  <button onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)} className={`absolute -bottom-2 ${mine ? "-left-2" : "-right-2"} w-6 h-6 rounded-full card-surface flex items-center justify-center text-slate-300 opacity-100 sm:opacity-0 sm:group-hover:opacity-100`} data-testid={`chat-react-btn-${m.id}`}><Smile className="w-3.5 h-3.5" /></button>
                  {pickerFor === m.id && (
                    <div className="absolute z-20 top-full mt-1 card-surface rounded-full px-2 py-1 flex gap-1">
                      {EMOJIS.map((e) => <button key={e} onClick={() => react(m.id, e)} className="hover:scale-125 transition-transform">{e}</button>)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  {Object.entries(m.reactions || {}).map(([e, users]) => (
                    <button key={e} onClick={() => react(m.id, e)} className="text-xs card-surface rounded-full px-2 py-0.5 flex items-center gap-1" data-testid={`reaction-${m.id}-${e}`}>{e} <span className="text-slate-400">{users.length}</span></button>
                  ))}
                  {isAdmin && (
                    <div className="flex items-center gap-1 ml-1">
                      {!mine && (
                        <button onClick={() => mute(m)} title="Mute sender 24h" className="w-6 h-6 rounded-full card-surface flex items-center justify-center text-slate-400 hover:text-amber-400" data-testid={`chat-mute-${m.id}`}><VolumeX className="w-3.5 h-3.5" /></button>
                      )}
                      <button onClick={() => del(m)} title="Delete message" className="w-6 h-6 rounded-full card-surface flex items-center justify-center text-slate-400 hover:text-red-400" data-testid={`chat-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {image && (
        <div className="mb-2 relative inline-block"><img src={fileUrl(image)} alt="" className="h-20 rounded-lg" /><button onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-obsidian rounded-full p-1"><X className="w-3 h-3 text-white" /></button></div>
      )}
      <form onSubmit={send} className="flex items-end gap-2 glass rounded-2xl p-2 border border-gold/20">
        <button type="button" onClick={() => fileRef.current?.click()} className="p-2 text-gold" data-testid="chat-image-button"><ImagePlus className="w-5 h-5" /></button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        <AutoTextarea value={text} onChange={(e) => setText(e.target.value)} placeholder={uploading ? "Uploading image…" : "Flex your cards, say hi…"} className="flex-1 bg-transparent text-white placeholder-slate-500 focus:outline-none text-sm" data-testid="chat-input" />
        <button type="submit" className="gold-gradient text-obsidian p-2.5 rounded-xl" data-testid="chat-send"><Send className="w-4 h-4" /></button>
      </form>
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
